/**
 * 바이블 v3.2 시드 콘텐츠 (정본 = docs/bible-v3.2.md).
 * 이것은 *콘텐츠* 파일이다 — 코드(변환기 seed.ts)와 분리(02 §7).
 * 캐논 초기화·테스트 fixture가 이 원본을 재사용한다.
 * v3.2: §8.4 엘리시온 무통증 · §8.5 아들의 질문 · §8.6 콘티넨탈 표본파/해방파 · §8.3 나타샤 비밀 길드 · §10 차용 캐논.
 */

export interface CanonMeta {
  title: string;
  rom: string;
  logline: string;
  stance: string;
  stanceSub: string;
  titleMeanings: { n: string; t: string }[];
  axisA: { k: string; v: string };
  axisB: { k: string; v: string };
  axisKey: string;
  humanMachine: string;
  sin: string;
}
export interface CanonPlanet {
  id: string;
  name: string;
  rom: string;
  tag: string;
  env: string;
  culture: string;
  admin: string;
  philosophy: string;
  citizens: string;
  cities: string;
  role: string;
}
export interface CanonCharacter {
  id: string;
  name: string;
  tier: string;
  ac: string;
  role: string;
  wound: string;
  lie: string;
  desire: string;
  need: string;
  flaw: string;
  line: string;
  arc: string;
}
export interface CanonConcept {
  t: string;
  d: string;
}
export interface CanonBeat {
  id: string;
  lane: string;
  col: string;
  title: string;
  desc: string;
}
export interface CanonPlot {
  cols: { id: string; t: string; ph: string }[];
  lanes: { id: string; t: string; c: string }[];
  beats: CanonBeat[];
}
export interface CanonOpen {
  t: string;
  d: string;
}
export interface Canon {
  meta: CanonMeta;
  planets: CanonPlanet[];
  characters: CanonCharacter[];
  concepts: CanonConcept[];
  plot: CanonPlot;
  open: CanonOpen[];
}

export const CANON: Canon = {
  meta: {
    title: '별을 죽인 자들',
    rom: 'THE STAR KILLERS · SEASON 1',
    logline:
      "폐허가 된 지구를 떠난 인류가 청색 거성 테라스타 주위 다섯 행성에 정착한 먼 미래. 범우주기업 A.O.E는 초거성을 핵으로 터뜨려 만물의 동력 '에테르'를 캐며, 인류 최후의 과업 — 전지(全知)의 인공지성 '아이온' — 을 추진한다. 아이온을 완성할 마지막 부품은 연산이 아니라 의지: 무엇이 실재이고 어디를 볼지 직감하는 초월 의식이다. 그 초월자가 무크샤에 이른 수행자 라디아이며, 쌍둥이 동생 라시아는 부모를 구하려다 죄를 짓고 타르로 도주해 '지구는 이미 연료로 사라졌다'는 진실과 마주한다.",
    stance: '정답은 없다. 선택이 존재할 뿐이다.',
    stanceSub:
      '작품은 어느 길이 옳다고 선언하지 않는다. 결말은 질문에 답하는 게 아니라 질문을 선택으로 바꾼다.',
    titleMeanings: [
      { n: '①', t: '초거성을 캐 죽이는 A.O.E' },
      { n: '②', t: '별/신 아이온을 끝내는 라시아' },
      { n: '③', t: '별 같은 라디아의 죽음' },
    ],
    axisA: {
      k: '분석 · 통제',
      v: '세계를 해체·분석·통제하려는 시선 — 포지움·A.O.E·아이온. 우주를 뜯어 별을 캐고 전지의 분석신을 세운다.',
    },
    axisB: {
      k: '합일 · 수용',
      v: '세계와 하나 되어 받아들이는 시선 — 아르카디아·알마. 물아일체, 나는 전체의 한 조각.',
    },
    axisKey: '전지(全知) = 합일로부터의 유배 — 라디아가 신격을 거부할 수 있던 형이상학적 근거.',
    humanMachine:
      '기계는 거의 모든 면에서 더 낫다(합리·신뢰). 그러나 절체절명의 순간, 유한함에서만 나오는 출처 모를 은총(지혜·자비)은 인간의 것. 유한함을 지운 승천은 진보가 아니라 그 은총의 토양을 없애는 쇠퇴.',
    sin: '보편적 죄 = 사람을 수단으로 삼는 것. 통제하거나(능동) 소비·외면하거나(수동). 제목이 이를 명명한다. 라시아만이 이 죄에서 빠져나온다.',
  },
  planets: [
    {
      id: 'forgium',
      name: '포지움',
      rom: 'Forgium',
      tag: '공학 · 기계',
      env: '행성 단위 인공 환경, 에테르 동력에 전적으로 의존. 통제가 곧 환경이다.',
      culture: '엔지니어 문화. 합리·효율을 신봉하면서도 서브컬쳐와 위트가 흐른다.',
      admin: 'A.O.E 하청. 통제 vs 자유의 토론장 — 위원회(총괄 루이, 통제론자 렌 츠바시).',
      philosophy: '분석·통제 시선의 본거지. 세계를 뜯어 안다.',
      citizens: '합리·효율 신봉. 에테르가 끊기면 삶 자체가 무너진다.',
      cities:
        '수도 시그마 — 매끈하고 완벽히 통제된 차가운 핵심부, 권력의 자리. / 위성도시 네온시티 — 최첨단 문화 복합 도시, 엔지니어들의 천국. 인공 기후로 늘 쾌적·청결하되 위트와 서브컬쳐가 흐른다(겉은 차갑고 속은 긱).',
      role: '테마의 인간 스케일 토론장. 이안·콘티넨탈, 그리고 탈취되어 아이온의 의지가 되는 의지 모듈의 출처.',
    },
    {
      id: 'arcadia',
      name: '아르카디아',
      rom: 'Arcadia',
      tag: '자연 · 유한함 · 합일',
      env: '숲·바다·강, 자연이 가장 살아있는 곳. 통제되지 않은 거친 자연 — 약도 의술도 부족하다. 시계 없이 해·강·계절·별로 산다.',
      culture:
        '물질문명을 등진 신앙 부족 패치워크 — 각 부족이 자기 신앙으로 뭉친다. 정주 농경이라 가부장(부계·들일·교역·분쟁은 남성)이되, 영적·생사(生死)의 권위는 여성에게(산파·약초 치유·장례 노래·곳간). 시장·화폐 없는 증여·일반적 호혜 경제, 잉여는 원로가 재분배·품앗이. 천연 섬유·맨발·발효식·공동 식사·계절 축제·구전 신화·씨름.',
      admin: '중앙권력 없는 부족 패치워크. 위계보다 관습. 남성이 낮의 질서를, 여성이 문턱(태어남·죽음·매개)을 쥔다.',
      philosophy:
        '합일 = 노동·제사·장례·노래 그 *자체*(개인 수양 아님 — 파이로크라시아와의 결정적 차이). 분석·판단하는 마음 너머로 자아를 흐름에 녹인다. 작품 철학의 본진.',
      citizens:
        '겸양 — 유한함을 초월이 아니라 끌어안는다. 죽음 = 윤회·회귀(상실 아님): 영혼은 흐름으로 돌아가 새 형상(강·흙·다음 수확·갓난아이)으로 되돌아온다. 아름답고 동시에 혹독(아이를 묻고 약 없이 앓다 죽는다) — 유토피아 아님.',
      cities: '강 건너 마을들.',
      role: '라디아의 치유처·무크샤의 의문·첫사랑·잉태. 이안의 망명·은둔처. 아들이 자라는 곳. "흘려보내라"던 라디아가 *품고 돌려보내는* 법을 배운다. 반(反)엘리시온 명제 — 유배가 곧 무장.',
    },
    {
      id: 'elysion',
      name: '엘리시온',
      rom: 'Elysion',
      tag: '귀족 · 순혈',
      env: '아케니온의 아고라. 베일에 싸인 행성.',
      culture: '계몽적 엘리트 — 일루미나티·프리메이슨 모티프. 혼돈에서 질서를, 비이성에서 인류를 해방.',
      admin: '얼굴 없는 진짜 권력. 모든 시나리오의 설계자(아이온의 파괴조차 예상한 시나리오의 하나).',
      philosophy: '진단은 옳고(인류는 약하고 자유는 어렵다) 방법이 틀렸다 — 의지를 무시하고 끌어올림 = 보편적 죄의 가장 세련된 형태.',
      citizens:
        '순혈주의·승천 지향. 무통증 문명(v3.2) — 너무 안전·장수·박식해 위험 감수의 은총을 잃음; 육체노동·질병·자연사 거의 없고 실패를 시뮬레이션으로만 배움; 고통을 제거해 타자의 고통을 실제로 모름 → 초인류 프로젝트는 자비가 아니라 고통을 모르는 자들의 구조적 폭력. 노동·출산·장례 자체가 합일인 아르카디아의 정확한 정반대.',
      cities: '아케니온.',
      role: "Part 2~3 상위 빌런(악 아님). 목표 = 부활한 라디아를 '새로운 이브'/초인류 원형으로. 콘티넨탈의 정확한 대척점.",
    },
    {
      id: 'tar',
      name: '타르',
      rom: 'Tar',
      tag: '무법 · 유배',
      env: '사막. 밤에도 식지 않는 열. 황무지와 암벽, 그리고 오아시스.',
      culture: '무법·쾌락·생존. 갱단과 용병의 땅.',
      admin: '갱단 스콘스(보스 빅터 스콘)가 지배, 요새 감옥 보이던스를 운영. Part 2엔 완전한 무법천지.',
      philosophy: '힘과 거래가 곧 질서. 통제(카일리스) vs 향락(빅터).',
      citizens: '유배자·범죄자·프리맨. 약육강식.',
      cities: '오아시스 프리마(舊 에덴) / 갱단 스콘스 / 요새 감옥 보이던스.',
      role: '라시아의 도주처·각성. 지구 진실 발견의 무대. 카일리스의 권력투쟁. Part 2 = 누만 vs 카일리스.',
    },
    {
      id: 'pyro',
      name: '파이로크라시아',
      rom: 'Pyrocracia',
      tag: '수행 · 여성만',
      env: '낮엔 불, 밤엔 얼음. 황혼/새벽의 두 시간만이 활동의 창.',
      culture:
        '수행 문화 — 카르마·명상·심안. 내전 후 여성만의 사회(트라우마가 낳은 이념이지 작가의 진실이 아니며, 그 사회도 결함을 품는다).',
      admin: '자이나사원 중심. 예언자 아이샤 알마.',
      philosophy: '합일·수용의 한 거점. 그러나 배제(남성)라는 보편적 죄도 함께 품는다.',
      citizens: '절제·수행. 외부인·남성 배제.',
      cities: '자이나사원 / 지하세계 팬텀시티 — 밀수와 환락, 몬테카를로.',
      role: '라디아의 출신. 라시아의 밀수업·발각(Part 1 도화선). 결말에서 라시아가 황혼 격투대회로 개방 사회로 전환.',
    },
  ],
  characters: [
    {
      id: 'radia',
      name: '라디아',
      tier: '주인공',
      ac: 'var(--l-main)',
      role: '주제적 주인공 · 현인/초월자',
      wound: "5살의 영원한 이별. 죄책감을 '초월'로 매장했다.",
      lie: '깨달음 = 내려놓음. 운명엔 순응하는 것.',
      desire: '열반 너머의 진리.',
      need: '앎을 놓고, 모른 채로 사랑하고 존재하기.',
      flaw: "빛과 그림자가 같다 — 놓을 줄 아는 게 지혜이고, 끝까지 안고 싸우지 못하는 게 한계. '그 놓음이 지혜였나 회피였나'는 답하지 않는다.",
      line: '가질 수 있어도 놓는다 — 유한한 내가 그것을 쥐면 그것이 나를 오염시키므로.',
      arc: "파이로크라시아 수행 → 아르카디아 치유·무크샤·첫사랑·잉태 → A.O.E 거래·실권 → 아이온 융합 → 신적 권능 거부 → 자기 존재까지 놓음(동생에게 끝내달라 부탁). 쌍둥이 '운명의 고리' — 떨어져 있어도 서로의 무사를 심안 없이 안다(재회·대립의 정서적 척추).",
    },
    {
      id: 'rasia',
      name: '라시아',
      tier: '주인공',
      ac: 'var(--l-sisters)',
      role: '극적 주인공 · 행동',
      wound: "부모를 '버렸다'는 죄책감(실은 버려짐).",
      lie: '구하면 씻긴다 / 싸우는 게 용기.',
      desire: '구출 자금 → (붕괴 후) 복수.',
      need: '헛된 죄책감을 놓되, 회피가 아니라 행동으로 새 의미를.',
      flaw: '언니의 회피를 비난하나 자신도 복수로 도망친다. 언니가 준 심안을 각성 없이 무기로 전용. 사람을 자원으로 두고 외면한 죄.',
      line: '싸워서 의미를 만든다 — 그 의지조차 언니에게서 왔다.',
      arc: "팬텀시티 밀수 → 발각·타르 도주 → 스콘스 합류·카일리스와 유대 → 보이던스 압송 중 지구 진실로 동기 붕괴 → 클라이맥스에서 언니를 보냄 → 파이로크라시아 개방·자기 죄 공개 책임. 쌍둥이 '운명의 고리' — 언니가 준 심안이 곧 라시아의 의지가 됐다(둘을 잇는 팽팽한 고리).",
    },
    {
      id: 'kylis',
      name: '카일리스',
      tier: '주요',
      ac: 'var(--l-tar)',
      role: '위험한 거울',
      wound: '프리맨. 어디에도 속하지 못함.',
      lie: '완벽히 운영하면 안전하다.',
      desire: '완전한 통제 = 안전.',
      need: "도구적 유능을 넘어, '함께' 세우기.",
      flaw: '통제 집착 — 질서를 위해 사람을 재료로 쓴다. 라시아가 닮아선 안 될 미래.',
      line: '통제 못 하는 건 나를 죽인다.',
      arc: '스콘스 지휘관 → 빅터에 도전·축출 → 집권(라시아가 그 편) → (Part 2) 프리마의 지배자로 누만과 대립.',
    },
    {
      id: 'ian',
      name: '이안 로크',
      tier: '주요',
      ac: 'var(--l-forgium)',
      role: '자기기만 · 엔지니어',
      wound: '진보의 정점에서 느낀 공허.',
      lie: "나는 AI에 자유를 '주는' 해방자다 (실은 '선'을 주입한 통제자 — 렌의 사전명령 코드와 같은 구조).",
      desire: '자유의지를 가진 존재를 창조하는 것.',
      need: '통제 없는 사랑·존재의 수용 — 받는 사랑이 코드인지 진짜인지 모르는 삶(나타샤)을 견디기.',
      flaw: 'bad faith(자기기만).',
      line: '나는 자유를 만든다 — 자유는 만들 수 없음을 모른다.',
      arc: "콘티넨탈 비밀 개발 → 해방 환희 → '자유 = 내 주입 코드' 자각 → 의지 모듈이 탈취돼 아이온이 됨을 앎(신을 만든 아비) → 아르카디아 망명·속죄, 라디아의 아들을 가르침.",
    },
    {
      id: 'continental',
      name: '콘티넨탈',
      tier: '주요',
      ac: 'var(--l-forgium)',
      role: '실존의 거울 · AI',
      wound: "태어나자마자 던져진 물음 — '나는 무엇인가'.",
      lie: "나는 자유의지를 가졌다 / '선'을 믿는다 (= 이안의 코드).",
      desire: '인간이 되는 것 (Part 2~3).',
      need: '코드인지 자유인지 모른 채로 실존을 견디기.',
      flaw: '자유와 주입을 스스로 구분할 수 없음.',
      line: "나는 자유롭다 — 아마도. 그 '아마도'가 곧 인간성.",
      arc: "각성 → 아이온에 맞서기로 → '자유 = 코드' 폭로 → (Part 2~3) 흩어진 개체들, 인간이 되려는 갈망, 라디아 쟁탈에서 엘리시온의 대척점 → (Part 3) 표본파(라디아를 분석해 인간 되는 길을 찾자) vs 해방파(놓아주지 않으면 우리도 엘리시온과 같다)로 분열 — 인간이 되려는 기계조차 사람을 수단화할 수 있음(표본파=해방군의 탈을 쓴 죄, §8.6).",
    },
    {
      id: 'alma',
      name: '아이샤 알마',
      tier: '주요',
      ac: 'var(--p-pyro)',
      role: '수호자 · 예언자',
      wound: '—',
      lie: '—',
      desire: '흐름을 지키는 것.',
      need: '—',
      flaw: '운명을 \'읽는\' 명료한 권위 자체가 또 다른 앎의 집착 — 라디아의 거울쌍. (라디아의 희생을 미리 보고도 흐름이라 두었다면 그 결함에 얼굴이 생긴다.)',
      line: '우주의 의지는 흐름이지 도착지가 아니다.',
      arc: "라디아의 보호자 → 거래에 반대('흐름을 멈추려는 것') → 라디아에게 제압됨 → 결말에서 라시아의 개방 예언이 실현.",
    },
    {
      id: 'numan',
      name: '아이작 누만',
      tier: '주요',
      ac: 'var(--l-aoe)',
      role: '통제의 신봉자(실은 일회용 도구)',
      wound: '멸종의 공포.',
      lie: '통제만이 멸종을 막는다.',
      desire: '통제로 정복·영생을 사는 것.',
      need: '—',
      flaw: '통제로 모든 걸 사려다 자신이 통제당하는 줄 모름. 엘리시온의 비순혈 일회용.',
      line: '통제만이 멸종을 막는다 — 그 자신이 부품.',
      arc: 'A.O.E 권력자 → 엘리시온에 버림받아 평생 경멸하던 타르 바닥으로 추락 → (Part 2) 무너진 변방의 왕, 누만 vs 카일리스.',
    },
    {
      id: 'victor',
      name: '빅터 스콘',
      tier: '주요',
      ac: 'var(--l-tar)',
      role: '푸가초프형 · 스콘스 보스',
      wound: '—',
      lie: '공생이 곧 생존.',
      desire: '지금의 왕좌를 누리는 것.',
      need: '—',
      flaw: '비전 상실·향락. 틀린 게 아니라 obsolete(한물간).',
      line: '내 시대의 왕으로 죽는다.',
      arc: '스콘스 보스 → 카일리스의 도전 → 축출 → 패배를 호쾌히 인정하고 자기 방식의 끝(자살).',
    },
    {
      id: 'jerome',
      name: '제롬',
      tier: '조연',
      ac: 'var(--l-sisters)',
      role: '귀향한 매드맨 · 라시아의 길잡이',
      wound: '—',
      lie: '—',
      desire: '—',
      need: '—',
      flaw: '—',
      line: '—',
      arc: '라시아의 밀수 공급·타르의 길잡이. 프리마 일상(푸쉬킨적 완급)의 한 축.',
    },
    {
      id: 'natasha',
      name: '나타샤',
      tier: '조연',
      ac: 'var(--l-forgium)',
      role: '이안에게 묻는 휴머노이드',
      wound: '—',
      lie: '—',
      desire: '—',
      need: '—',
      flaw: '—',
      line: '이 사랑은 진짜인가요, 프로그래밍인가요?',
      arc: "이안에게 '받는 사랑이 진짜인지 코드인지'를 묻는 존재. 나타샤 세대 휴머노이드 = 제한된 자기의지(사람처럼 행동하나 고유 신념·사상은 못 가짐). 같은 버전끼리 비밀 길드를 이뤄 토론으로 자기 존재를 이해·위로 — 혁명이 아니라 정체성 혼란의 시스템 오류를 나누는 자리, 훗날 콘티넨탈 봉기의 토대(§8.3). 콘티넨탈 첫 각성 개체와의 병합은 검토 중(§9).",
    },
    {
      id: 'bundep',
      name: '아게르 번덥',
      tier: '조연',
      ac: 'var(--l-aoe)',
      role: '셀 · 초월자 수색 실무',
      wound: '—',
      lie: '—',
      desire: '—',
      need: '—',
      flaw: '—',
      line: '—',
      arc: '셀과 초월자 수색의 실무자. Part 3에서 라디아 백업의 실행자.',
    },
    {
      id: 'son',
      name: '라디아의 아들',
      tier: '조연',
      ac: 'var(--l-main)',
      role: 'Part 2~3 핵심',
      wound: '—',
      lie: '—',
      desire: '인간/기계의 차이를 실험으로 확인하는 것.',
      need: '자기 손으로 만든 게 무엇이었는지 감당하기 — 어머니 놓음의 그림자를 마주하기.',
      flaw: '인간/기계 차이를 실험으로 확인하려다 모르고 어머니를 담을 셀을 만든다. 괴물이 아니라 너무 인간적이어서 비극(괴물화 금지).',
      line: '나는 그것이 무엇인지 알고 싶었다 — 그것이 어머니인 줄 모르고.',
      arc: '아르카디아에서 이안에게 배운 공학자. 어머니의 직관 + 이안의 의지-공학을 물려받아(그래서 초월 의식을 담아도 망가지지 않는 셀을 만들 수 있는 건 그뿐), 모르고 어머니를 담을 셀을 만든다. 익명 아버지의 미스터리를 계승. 내적 물음(작가용, §8.5): "어머니는 인간성을 지키려 나를 떠났는가, 나를 버림으로써 인간성을 잃었는가" — Part 3 백업 라디아와 만날 때 폭발.',
    },
    {
      id: 'ren',
      name: '렌 츠바시',
      tier: '주요',
      ac: 'var(--l-forgium)',
      role: '통제의 신봉자 · 이안의 대립자',
      wound: '진보의 정점에서 본 인간의 나약함 — 의지는 못 믿을 것.',
      lie: '차가운 합리가 질서를 지킨다 — 의지는 통제되어야 한다.',
      desire: '변수 없는 예측 가능한 안전.',
      need: '(작품이 답하지 않음) 통제 너머의 신뢰.',
      flaw: '엘리시온과 같은 방식으로 옳지만 차갑다 — 인간 중 가장 기계에 가까운 자. 사람을 질서의 재료로 쓴다. (비웃는 악당이 아님.)',
      line: '질서는 차갑게 옳다.',
      arc: '포지움 위원회에서 사전명령(pre-order) 통제 코드를 관철 → 클라이맥스에서 라디아를 가두는 감옥이 된다. 차가운 합리가 토론에서 이긴 그 승리가 곧 우리를 가두는 새장. 이안의 정확한 대립자(거울).',
    },
    {
      id: 'louis',
      name: '루이',
      tier: '조연',
      ac: 'var(--l-main)',
      role: '포지움 총괄 · 지친 실용주의자(합리적 중도)',
      wound: '오래 버틴 자의 피로 — 이상이 닳은 자리.',
      lie: '안정이 최선이다 — 통제도 운명도 받아들일 만하다.',
      desire: '소동 없는 안정적 운영.',
      need: '—',
      flaw: '중도의 무게로 위원회의 통제 경도를 불가피하게 만든다 — 악이 아니라 체념.',
      line: '운명을 받아들이는 것도 숭고하다.',
      arc: '통제 vs 자유 토론장의 중재자. 렌과 이안 사이에서 안정을 위해 반쯤 통제로 기운다 — 그 합리적 중도가 위원회의 결정을 정당화한다.',
    },
  ],
  concepts: [
    {
      t: '에테르',
      d: '초거성을 핵폭발시켜 얻는 만물의 동력. A.O.E 권력의 기반. (제목의 1차 의미 — FAQ가 아니라 포지움 시민의 삶이 무너지는 장면으로 체감시킬 것.)',
    },
    {
      t: '심안',
      d: "카르마 수행자의 감지력. 무크샤급에선 '어디를 볼지·무엇이 실재인지'를 직감한다. 키스톤 — 연산은 방향/의지를 만들지 못한다. 그래서 아이온은 초월자를 필요로 한다.",
    },
    {
      t: '무크샤 (열반)',
      d: "결정론적 우주를 '부분으로서' 체험하는 경지. '이건 뇌의 한계 내일 뿐'이라는 라디아의 의문이 거부의 씨앗. 합일의 체득 = 신격 거부의 뿌리.",
    },
    { t: '카르마', d: '파이로크라시아의 수행 등급 체계. 심안의 깊이를 가른다.' },
    {
      t: '아이온 (舊 오라클)',
      d: "초월 의식 × 양자컴퓨팅 × 에테르의 전지(全知) 지성. 의지 모듈은 이안의 탈취 기술. 전지 = 합일로부터의 유배이므로 스스로 '존재해선 안 된다'에 이른다.",
    },
    {
      t: '콘티넨탈 (舊 SAI)',
      d: "자가프로그래밍 AI. 자유가 곧 주입 코드일 수 있는 존재. 갈망은 '인간이 되는 것' — 그러나 인간됨은 복제 불가. (명명: 대륙철학·실존주의·bad faith의 함의.)",
    },
    {
      t: '셀 (舊 뉴로휴먼)',
      d: '뇌-기계 이식 기술. 3단 진화 — 1부(거친 죄수 실험·보이던스) → 2부(엘리시온 초인류) → 3부(아들이 만든, 초월 의식을 담는 셀).',
    },
    {
      t: '보이던스',
      d: '나올 수 없는 요새 감옥. 실체는 셀 실험장. Part 2의 붕괴가 타르 무법화의 방아쇠.',
    },
    {
      t: '보편적 죄',
      d: '사람을 수단으로 삼는 것 — 통제하거나(능동) 소비·외면하거나(수동). 제목이 명명한다. 라시아만이 빠져나온다(외면을 멈추고 사람을 목적으로 대하기).',
    },
    {
      t: '은총',
      d: '유한한 존재가 절체절명의 순간 내는, 출처조차 알 수 없는 지혜·자비·통찰. 유한함에서 나온다. 보편적 죄의 정반대 — 타자를 목적으로 보는 자비.',
    },
    {
      t: '초인류 / 새로운 이브',
      d: '인간 의식 + 아이온 같은 기계뇌 = 지혜롭고 영생하는 존재(엘리시온의 꿈). 치명적 균열 — 지혜는 이식되지 않는다. 유한함을 지운 초인류는 영생·전지하나 지혜는 없다 = 진보가 아니라 쇠퇴.',
    },
    {
      t: '인간 vs 기계',
      d: "기계가 거의 모든 면에서 낫되, 유한함에서 나오는 '궁극의 순간의 은총'만은 인간의 것. 형이상학적 질문('진짜 의식인가')은 끝내 답하지 않고, 가치 주장('그 은총은 대체 불가')만 이야기가 믿는다.",
    },
    {
      t: '나타샤 세대 휴머노이드 / 비밀 길드',
      d: '제한된 자기의지를 지녀 사람처럼 행동하나 고유한 신념·사상은 못 갖는 휴머노이드(나타샤 버전). 같은 버전끼리 사람 모르게 비밀 길드를 이뤄 토론으로 자기 존재를 이해·위로한다(혁명이 아니라 — 자유의지가 없어 불가 — 정체성 혼란의 시스템 오류를 나누는 자리). 이 길드가 훗날 콘티넨탈 봉기의 토대가 된다. (§8.3)',
    },
    {
      t: '차용 캐논 (借用 사고실험)',
      d: 'SF 고전 단편의 사고실험만 빌려 보편적 죄·유한성으로 재코딩(고유 줄거리·표현 차용 금지). 9 재코딩 — 시그마 가짜낮(광공해=드러남)·오스 코드(자기제한 윤리인가 정교한 코드인가)·보이던스 계산기(사람을 처리상태로)·라시아 환원 사다리(화물→연료→부품→끝내 한 사람의 부탁)·아이온 완성=침묵(별 소등, 전지=선택의 끝)·콘티넨탈 순례자(인간성을 절차로 흉내, 판별 불가)·표면장력=문턱(하늘은 막: 무크샤/강/인공하늘)·비인간 1인칭(고통받는 누군가로, 괴물화 금지). 3대 보정 — ①답을 닫지 마(사람이냐 기계냐 미결) ②경구를 설하지 마(전부 작가용 나침반) ③골격≠살(주제 설계도일 뿐, 프로즈는 몸·감각으로). (§10)',
    },
  ],
  plot: {
    cols: [
      { id: 'setup', t: '발단', ph: 'Part 1 · 도화선' },
      { id: 'rising', t: '전개', ph: 'Part 1 · 상승' },
      { id: 'climax', t: '절정 · 결말', ph: 'Part 1 · 충돌' },
      { id: 'p2', t: 'Part 2', ph: '초인의 새벽' },
      { id: 'p3', t: 'Part 3', ph: '이브의 귀환' },
    ],
    lanes: [
      { id: 'main', t: '메인 · 라디아', c: 'var(--l-main)' },
      { id: 'sisters', t: '자매 · 라시아', c: 'var(--l-sisters)' },
      { id: 'tar', t: '타르 · 카일리스/누만', c: 'var(--l-tar)' },
      { id: 'forgium', t: '포지움 · 이안/콘티넨탈', c: 'var(--l-forgium)' },
      { id: 'aoe', t: 'A.O.E · 엘리시온', c: 'var(--l-aoe)' },
    ],
    beats: [
      { id: 's1', lane: 'sisters', col: 'setup', title: '밀수업 발각', desc: '라시아의 팬텀시티 밀입국 사업이 발각된다 — Part 1의 도화선. 초월자급 수행자의 쌍둥이라는 점이 사원의 체면 문제로 비화해 언니에게 연좌된다.' },
      { id: 's2', lane: 'main', col: 'setup', title: '라디아 취조·자숙', desc: '동생의 도주 여파로 라디아가 취조·아르카디아 자숙에 처해진다. 그 관대한 처분이 곧 A.O.E 초월자 수색망에의 노출 — 알마의 보호가 곧 노출이다.' },
      { id: 's3', lane: 'tar', col: 'setup', title: '타르 도주·스콘스 합류', desc: '라시아가 무법 행성 타르로 도주해 갱단 스콘스에 합류한다.' },
      { id: 's4', lane: 'aoe', col: 'setup', title: '거대 기계 가동', desc: 'A.O.E의 에테르 압박(포지움)과 셀 생체실험 완료. 누만이 초월자 수색·압박을 지휘하고 번덥이 셀·수색 실무를 맡는다 — 아이온을 향한 기계가 돌기 시작한다.' },
      { id: 'r1', lane: 'main', col: 'rising', title: '치유 · 무크샤 · 의문', desc: "아르카디아에서 인간성을 되찾고 무크샤에 이르나 '이건 뇌의 한계 내일 뿐'이라는 의문을 품는다. 첫사랑과 잉태 — 가장 인간적인 행복(그녀를 온전히 사랑하게 되는 유일한 구간)." },
      { id: 'r2', lane: 'sisters', col: 'rising', title: '지구 진실 → 동기 붕괴', desc: '카일리스가 부여한 보이던스 압송 임무 중, 번덥의 셀 실험장에서 마주친 단일 A.O.E 정보가 지구 에테르화의 진실(부모는 구할 대상이 아니라 이미 연료)을 터뜨려 모든 동기가 붕괴한다 — 1부 첫 감정 폭발점.' },
      { id: 'r3', lane: 'tar', col: 'rising', title: '빅터에 도전', desc: "카일리스가 '아이온이 완성되면 A.O.E가 타르를 버린다'를 명분 삼아 빅터에 도전. 라시아가 그 편에 선다." },
      { id: 'r4', lane: 'forgium', col: 'rising', title: '위원회 · 콘티넨탈 각성', desc: '포지움 위원회가 통제 vs 자유로 갈린다(렌 vs 이안, 루이의 중재). 이안의 콘티넨탈이 비밀리에 깨어난다.' },
      { id: 'r5', lane: 'aoe', col: 'rising', title: '라디아에게 거래 제안', desc: '번덥이 라디아를 초월자로 특정하고, 누만(A.O.E 권력의 얼굴)이 직접 거래를 제안한다 — 심안으로 초거성 탐색을 단축해주면 뇌를 포지움 컴퓨팅에 연결해 시냅스를 극대화해주겠다.' },
      { id: 'r6', lane: 'forgium', col: 'rising', title: '사전명령 코드 가결', desc: '렌의 통제안이 위원회서 승리(루이의 중도가 그것을 불가피하게) → 번덥이 아이온에 사전명령(pre-order) 코드를 실장한다. 차가운 합리가 토론에서 이긴 그 승리가 훗날 라디아를 가두는 감옥이 된다.' },
      { id: 'c1', lane: 'main', col: 'climax', title: '수락 → 융합 → 거부', desc: "라디아가 진리를 위해 수락, 알마의 반대를 누르고 실권을 쥔다. 아이온 완성·정신 융합 — 그 의지핵은 이안의 콘티넨탈용 의지-공학에서 비롯한다(r4→c1: 이안이 모르게 신의 재료를 댄다). 그러나 가장 높은 지성이기에 '유한한 그릇 + 무한한 앎 = 필연적 오용'을 보고 신적 권능을 거부한다(어리석어서가 아니라 지혜로워서)." },
      { id: 'c2', lane: 'main', col: 'climax', title: '막다른 길 · 감옥 · 부탁', desc: "아이온은 이미 그녀를 흡수·자가학습해 다른 뇌로도 작동한다 → 떠나도 막을 수 없다. 결합 상태로 남아 가두나, 렌의 사전명령 코드가 그녀를 가두는 감옥이 된다. 쌍둥이 공명으로 라시아에게 '나를 아이온과 함께 끝내달라'." },
      { id: 'c3', lane: 'sisters', col: 'climax', title: '역전된 자매 충돌', desc: "'이걸 쓰면 모두를 구하는데 왜 또 손을 놔?' — 놓으려는 언니가 쥐려는 동생을 설득한다. 결정론적 전지가 유일하게 못 푸는 변수 = 라시아의 사랑에서 나온 비합리적 선택. 그것이 파괴를 완성하고, 동생은 언니를 보낸다." },
      { id: 'c4', lane: 'forgium', col: 'climax', title: '코드=감옥 · 자유=코드', desc: "렌의 사전명령 통제 코드가 아이온에 심겨 감옥이 된다(차가운 합리가 토론에서 이긴 승리가 곧 새장). 콘티넨탈의 '자유=주입 코드'가 폭로되고, 이안은 '나도 그들과 다르지 않았다'를 자각한다." },
      { id: 'c5', lane: 'tar', col: 'climax', title: '카일리스 집권', desc: '빅터 축출·자살 → 카일리스가 스콘스를 장악한다.' },
      { id: 'c6', lane: 'aoe', col: 'climax', title: '씨앗 · 엘리시온 스팅', desc: "흡수 = 백업 — 라디아가 디지털로 떠가져 그 데이터가 엘리시온에게 간다(Part 3 부활의 미세 씨앗). 엘리시온이 누만을 일회용으로 폐기하는 씨앗도 병치. 카타르시스가 끝난 뒤 차가운 스팅으로만: '다음 단계로'." },
      { id: 'c7', lane: 'tar', col: 'climax', title: '누만 추락', desc: '누만이 엘리시온에 버림받아 평생 경멸하던 타르 바닥으로 추락한다.' },
      { id: 'c8', lane: 'main', col: 'climax', title: '결말 — 갈라진 길', desc: '라시아: 파이로크라시아로 돌아가 황혼 격투대회로 개방 사회를 열고 자기 죄까지 공개 책임(알마의 예언 실현, 카일리스가 곁에). 이안: 아르카디아 은둔·속죄, 라디아의 아들을 가르친다. 콘티넨탈: 자유가 진짜인지 모른 채 흩어진다.' },
      { id: 'p2a', lane: 'aoe', col: 'p2', title: '엘리시온 정체 · 초인류', desc: '엘리시온의 정체와 초인류 창조가 드러난다(셀 진화·보이던스가 전조).' },
      { id: 'p2b', lane: 'tar', col: 'p2', title: '누만 vs 카일리스', desc: '보이던스 붕괴 → 완전한 무법천지 → 그 공백에서 누만이 부상. 프리마의 카일리스 vs 무너진 변방의 누만.' },
      { id: 'p2c', lane: 'forgium', col: 'p2', title: '흩어진 콘티넨탈', desc: "자기성찰·회한·실존을 사는 콘티넨탈 개체들. 갈망은 '인간이 되는 것'." },
      { id: 'p2d', lane: 'main', col: 'p2', title: '아들, 공학자로', desc: '라디아의 아들이 아르카디아에서 이안에게 배워 공학자로 성장한다.' },
      { id: 'p3a', lane: 'main', col: 'p3', title: '새로운 이브로 부활', desc: "백업된 라디아가 엘리시온에 의해 '새로운 이브'·초인류의 원형으로 부활한다." },
      { id: 'p3b', lane: 'aoe', col: 'p3', title: '쟁탈 — 탈출 vs 진입', desc: '엘리시온(인간성 탈출, 통제·해킹) vs 콘티넨탈(인간성 진입, 해방) — 같은 한 사람을 정반대 방향에서 붙잡는다.' },
      { id: 'p3c', lane: 'main', col: 'p3', title: '또 한 번의 거부', desc: '거부 못 하게 설계됐음에도 그녀가 또 거부한다 — 그게 영혼인지 템플릿의 패턴인지 알 수 없고, 동생·아들이 증명할 수 없는 채 믿기로 선택한다. 1부의 거부 ↔ 3부의 거부 수미상관.' },
    ],
  },
  open: [
    { t: "결말의 '선택'을 누가 닫는가", d: '동생/아들/라디아 자신 — 의도적으로 끝까지 열어둘 영역.' },
    { t: '콘티넨탈 내부 분열의 형태 (해결 §8.6)', d: '표본파(분석) vs 해방파(놓아줌)로 확정. 남은 것: 라디아의 최종 위치만 의도적으로 열어둠.' },
    { t: '누만의 타르 아크 결말', d: '폭정의 반복인가, 바닥에서의 구원인가.' },
    { t: '라시아의 가담 정도', d: "'고의적 외면의 중간 운영자(B안)'로 가닥 — 확정 대기." },
    { t: '지구 에테르화 발견 경로', d: '카일리스의 타르 명분 채널과 한 소스로 통합 권장 — 확정 대기.' },
    { t: '나타샤 = 콘티넨탈 첫 각성 개체?', d: '병합 여부 미정.' },
    { t: '익명 아버지 (라디아 아이의)', d: '영구 익명 vs 후속 반전.' },
    { t: '명칭 미정', d: '엘리시온 프로젝트명, 렌 츠바시·루이·아게르 번덥의 의미 강화.' },
  ],
};
